import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { MessageCircle, X, Send, Loader2, Shield, Info, ArrowLeft, LifeBuoy, AlertTriangle, Crown, Bug } from "lucide-react";
import { toast } from "sonner";
import { Link, useLocation } from "react-router-dom";
import AttachmentPicker, { UploadedAttachment } from "@/components/support/AttachmentPicker";
import SupportChatWindow from "@/components/support/SupportChatWindow";

interface Ticket {
  id: string;
  user_id: string;
  subject: string;
  status: "open" | "pending_admin" | "pending_user" | "closed";
  opened_at: string;
  closed_at: string | null;
  unread_user_count: number;
}

const SupportChatBubble = () => {
  const { user, role, subscriptionActive } = useAuth();
  const { getSetting } = useSiteSettings();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [active, setActive] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const [subject, setSubject] = useState("Demande au support");
  const [firstMessage, setFirstMessage] = useState("");
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
  const [creating, setCreating] = useState(false);

  const monthlyPrice =
    parseFloat(getSetting("pro_monthly_price", "6.99").replace(",", ".")) || 6.99;
  const monthlyPriceLabel = monthlyPrice.toFixed(2).replace(".", ",");

  // Draggable bubble — persisted position (offset from bottom-right corner).
  // NOTE: These hooks MUST stay above any early return to preserve hook order
  // across renders (fixes React error #310 when `visible` flips false→true).
  const POS_KEY = "cg_support_bubble_pos_v1";
  const [pos, setPos] = useState<{ right: number; bottom: number }>(() => {
    if (typeof window === "undefined") return { right: 16, bottom: 16 };
    try {
      const raw = localStorage.getItem(POS_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore invalid saved position */ }
    return { right: 16, bottom: 16 };
  });
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startRight: number;
    startBottom: number;
    moved: boolean;
    pointerId: number;
  } | null>(null);

  // Don't show for admins (they have the admin tab)
  const visible = !!user && role !== "admin";
  const showSubscriptionShortcut = role === "pro" && location.pathname !== "/subscription";

  const fetchTickets = async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from("support_tickets")
      .select("*")
      .eq("user_id", user.id)
      .order("last_message_at", { ascending: false });
    setTickets((data ?? []) as Ticket[]);
    setUnread(((data ?? []) as Ticket[]).reduce((acc, t) => acc + (t.unread_user_count || 0), 0));
    setLoading(false);
  };

  useEffect(() => {
    if (!visible) return;
    fetchTickets();
    const channel = supabase
      .channel(`support-tickets-user-${user!.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "support_tickets", filter: `user_id=eq.${user!.id}` },
        () => fetchTickets(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, visible]);

  if (!visible) return null;

  const createTicket = async () => {
    if (!firstMessage.trim() && attachments.length === 0) {
      toast.error("Écrivez un message ou ajoutez un fichier");
      return;
    }
    setCreating(true);
    const { data, error } = await supabase.functions.invoke("support-send-message", {
      body: {
        subject: subject.trim() || "Demande au support",
        content: firstMessage.trim(),
        attachments,
      },
    });
    setCreating(false);
    if (error || !data?.ticket_id) {
      console.error(error);
      toast.error("Création échouée");
      return;
    }
    toast.success("Conversation envoyée");
    setFirstMessage("");
    setAttachments([]);
    await fetchTickets();
    const fresh = await supabase
      .from("support_tickets")
      .select("*")
      .eq("id", data.ticket_id)
      .maybeSingle();
    if (fresh.data) setActive(fresh.data as Ticket);
  };

  const openTicket = async (t: Ticket) => {
    setActive(t);
    // mark messages read for user
    await supabase
      .from("support_tickets")
      .update({ unread_user_count: 0 })
      .eq("id", t.id);
  };


  const clampPos = (right: number, bottom: number) => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const size = 56; // button height / min touch target
    return {
      right: Math.max(8, Math.min(w - size - 8, right)),
      bottom: Math.max(8, Math.min(h - size - 8, bottom)),
    };
  };

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startRight: pos.right,
      startBottom: pos.bottom,
      moved: false,
      pointerId: e.pointerId,
    };
    (e.currentTarget as HTMLButtonElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) < 5) return;
    d.moved = true;
    setPos(clampPos(d.startRight - dx, d.startBottom - dy));
  };
  const onPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const d = dragRef.current;
    dragRef.current = null;
    try {
      (e.currentTarget as HTMLButtonElement).releasePointerCapture(e.pointerId);
    } catch { /* pointer capture may already be released */ }
    if (d?.moved) {
      try {
        localStorage.setItem(POS_KEY, JSON.stringify(pos));
      } catch { /* storage may be unavailable */ }
    } else {
      setOpen(true);
    }
  };

  return (
    <>
      {showSubscriptionShortcut && (
        <Link
          to="/subscription"
          aria-label={subscriptionActive ? "Gérer mon abonnement PRO" : "S'abonner à Click&Gard PRO"}
          title={subscriptionActive ? "Gérer, modifier ou résilier mon abonnement" : "Choisir mon abonnement PRO"}
          style={{ right: `${pos.right}px`, bottom: `${pos.bottom + 68}px` }}
          className="fixed z-[449] h-12 px-4 rounded-full border border-primary/35 bg-background/95 shadow-lg backdrop-blur-md flex items-center gap-2 text-foreground hover:border-primary hover:shadow-neon hover:scale-[1.02] transition-all"
        >
          <Crown className="w-5 h-5 shrink-0 text-primary" />
          <span className="text-sm font-semibold whitespace-nowrap">
            {subscriptionActive ? "Gérer mon abonnement" : `S'abonner — ${monthlyPriceLabel} €/mois`}
          </span>
        </Link>
      )}

      <button
        type="button"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        aria-label="Support Click&Gard — glissez pour déplacer, cliquez pour ouvrir"
        style={{ right: `${pos.right}px`, bottom: `${pos.bottom}px`, touchAction: "none" }}
        className="fixed z-[450] h-14 pl-3 pr-4 rounded-full bg-gradient-to-br from-primary to-secondary shadow-neon flex items-center gap-2 text-primary-foreground hover:scale-105 transition-transform cursor-grab active:cursor-grabbing select-none"
      >
        <LifeBuoy className="w-6 h-6 pointer-events-none" />
        <span className="text-sm font-semibold tracking-wide pointer-events-none">Support</span>
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-xs font-bold ring-2 ring-background pointer-events-none">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col z-[650]">
          <SheetHeader className="px-4 py-3 border-b border-border shrink-0">
            <SheetTitle className="flex items-center gap-2 text-base">
              {active && (
                <button onClick={() => setActive(null)} className="p-1 -ml-1 rounded hover:bg-muted">
                  <ArrowLeft className="w-4 h-4" />
                </button>
              )}
              <Shield className="w-5 h-5 text-primary" />
              {active ? "Conversation support" : "Support Click&Gard"}
            </SheetTitle>
          </SheetHeader>

          {!active ? (
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className="rounded-xl bg-amber-500/10 border border-amber-500/40 p-3 text-xs flex gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <span className="text-foreground">
                  <strong>Ceci est le support technique Click&Gard</strong> (équipe interne).<br />
                  Pour contacter directement un <strong>professionnel</strong> ou un <strong>parent</strong>, utilisez la <Link to="/messages" onClick={() => setOpen(false)} className="underline font-semibold text-primary">messagerie privée</Link> depuis son profil — <em>pas ce formulaire</em>.
                </span>
              </div>
              <div className="rounded-xl bg-primary/5 border border-primary/15 p-3 text-xs flex gap-2">
                <Info className="w-4 h-4 text-primary shrink-0" />
                <span>
                  Discutez avec notre équipe support (bug, question sur votre compte, litige…). Délai de réponse jusqu'à <strong>24h</strong>. Pièces jointes acceptées (25 Mo max).
                </span>
              </div>

              <Link
                to={"/signaler-un-bug?from=" + encodeURIComponent(location.pathname + location.search)}
                onClick={() => setOpen(false)}
                className="flex items-center justify-between gap-3 rounded-xl border border-destructive/25 bg-destructive/5 p-3 hover:bg-destructive/10 hover:border-destructive/40 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-destructive/10 flex items-center justify-center">
                    <Bug className="w-4 h-4 text-destructive" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">Signaler un bug</p>
                    <p className="text-xs text-muted-foreground">Accessible même sans abonnement PRO actif</p>
                  </div>
                </div>
                <span className="text-sm text-muted-foreground">→</span>
              </Link>

              {loading ? (
                <div className="text-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" />
                </div>
              ) : tickets.length > 0 ? (
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">
                    Vos conversations
                  </h4>
                  <ul className="space-y-1.5">
                    {tickets.map((t) => (
                      <li key={t.id}>
                        <button
                          onClick={() => openTicket(t)}
                          className="w-full text-left p-3 rounded-xl border border-border hover:border-primary/40 hover:bg-muted/40 transition-colors"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold truncate flex-1">{t.subject}</span>
                            {t.unread_user_count > 0 && (
                              <span className="min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold">
                                {t.unread_user_count}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {t.status === "closed"
                              ? `Clôturée le ${new Date(t.closed_at!).toLocaleDateString("fr-FR")}`
                              : t.status === "pending_admin"
                                ? "En attente du support"
                                : t.status === "pending_user"
                                  ? "Réponse reçue"
                                  : "Ouverte"}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/support"
                    onClick={() => setOpen(false)}
                    className="block text-center text-xs text-primary hover:underline mt-3"
                  >
                    Voir toutes mes conversations →
                  </Link>
                </div>
              ) : null}

              <div className="space-y-2 pt-2 border-t border-border">
                <h4 className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                  Nouvelle demande
                </h4>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Sujet"
                  maxLength={200}
                />
                <Textarea
                  value={firstMessage}
                  onChange={(e) => setFirstMessage(e.target.value)}
                  placeholder="Décrivez votre demande…"
                  rows={4}
                  maxLength={5000}
                />
                <AttachmentPicker
                  bucket="support-attachments"
                  value={attachments}
                  onChange={setAttachments}
                  compact
                  disabled={creating}
                />
                <Button onClick={createTicket} disabled={creating} className="w-full rounded-xl">
                  {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Envoyer au support
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-hidden p-2">
              <SupportChatWindow ticket={active} compact onTicketChange={(t) => setActive(t as Ticket)} />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};

export default SupportChatBubble;
